import { useState, useCallback, useEffect } from 'react'
import {
  Toolbar,
  SaveButton,
  List,
  SimpleForm,
  DateInput,
  DateField,
  TextInput,
  Datagrid,
  Edit,
  Create,
  TextField,
  ReferenceInput,
  NumberInput,
  NumberField,
  SelectInput,
  required,
  Show,
  SimpleShowLayout,
  FormDataConsumer,
  AutocompleteInput,
  BooleanInput,
  SearchInput,
  useCreate,
  useRecordContext,
  useNotify,
  useRedirect,
} from 'react-admin'
import { makeStyles } from '@material-ui/core/styles'
import { Dialog } from '@material-ui/core'
import { Visibility as VisibilityIcon } from '@mui/icons-material'
import { ExtraPagination } from './utils'
import FileWithContext from './models/file_with_context'
import UpsertFileButton from './components/file_upload'
import { TranslateWrapperTextField } from './components/translatable_field_wrapper'
import Api from './Apis/api.service'
import config from './config'

const useStyles = makeStyles({
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  icon: {
    '&:hover': {
      color: 'lightgrey',
    },
  },
})

export const CustomToolbar = (props) => (
  <Toolbar {...props} classes={useStyles()}>
    <SaveButton />
  </Toolbar>
)

const ContractFilter = [
  <SearchInput source="q" alwaysOn />,
  <TextInput fullWidth source="id" label="Ref" />,
  <SelectInput
    label="Type de contrat"
    source="type"
    allowEmpty
    emptyValue={null}
    choices={[
      { id: 'Syndic', name: 'Syndic' },
      { id: 'General', name: 'General' },
      { id: 'Assurance', name: 'Assurance' },
    ]}
  />,
]

function ContractDialog(props) {
  const { contractSrc, onClose, open } = props

  return (
    <Dialog
      onClick={(e) => e.stopPropagation()}
      onClose={onClose}
      fullWidth="true"
      maxWidth="md"
      open={open}
    >
      <embed
        src={contractSrc + '#toolbar=0'}
        type="application/pdf"
        width="1000px"
        height="600px"
      />
    </Dialog>
  )
}

const ContractFileField = ({ source }) => {
  const [open, setOpen] = useState(false)
  const record = useRecordContext()
  const classes = useStyles()
  const url = record[source]

  const handleDialogOpen = (e) => {
    e.stopPropagation()
    setOpen(true)
  }

  const handleClose = (e) => {
    setOpen(false)
  }

  return (
    <div>
      <VisibilityIcon
        className={classes.icon}
        onClick={handleDialogOpen}
        color="primary"
      />
      <ContractDialog contractSrc={url} open={open} onClose={handleClose} />
    </div>
  )
}

export const ContractsList = (props) => (
  <List
    {...props}
    exporter={false}
    pagination={<ExtraPagination />}
    perPage={100}
    filters={ContractFilter}
  >
    <Datagrid rowClick="edit" bulkActionButtons={false}>
      <NumberField source="id" label="Id" />
      <TextField source="libelle" label="Copro" />
      <TextField source="code" label="Code" />
      <TranslateWrapperTextField
        source="type"
        label="Type de contrat"
        prefix={'hs.file'}
      >
        <TextField />
      </TranslateWrapperTextField>
      <TextField source="upload_by" label="Prestataire" />
      <DateField source="created" label="Date de création" />
      <DateField source="deadline" label="Date d'échance" />
      <ContractFileField source="url" label="Contrat" />
    </Datagrid>
  </List>
)

const EditCommon = ({ setContractFileData, coproInternalId, ...props }) => {
  return (
    <>
      <FormDataConsumer {...props}>
        {({ formData, ...rest }) => {
          if (formData.type !== 'General') {
            return null
          }
          return (
            <>
              <ReferenceInput
                source="typegeneral_presta_id"
                label="Prestataire"
                reference="prestas"
                variant={props.variant}
              >
                <SelectInput optionText="name" fullWidth />
              </ReferenceInput>
              <br />
              <ReferenceInput
                source="typegeneral_presta_id"
                label="Prestataire"
                reference="prestas"
                variant={props.variant}
              >
                <AutocompleteInput
                  optionText="name"
                  fullWidth
                  matchSuggestion={() => true}
                />
              </ReferenceInput>
              <br />
              <NumberInput
                source="typegeneral_duree"
                label="Durée"
                fullWidth
                variant={props.variant}
              />
              <br />
              <SelectInput
                source="typegeneral_type_renouvellement"
                label="Type de renouvellement"
                variant={props.variant}
                fullWidth
                choices={[
                  { id: 'Non', name: 'Non' },
                  { id: 'A la demande', name: 'A la demande' },
                  { id: 'Tacite', name: 'Tacite' },
                ]}
              />
              <BooleanInput
                source="typegeneral_remettre_concurrence"
                label="A remettre en concurrence?"
                variant={props.variant}
              />
            </>
          )
        }}
      </FormDataConsumer>
      <FormDataConsumer {...props}>
        {({ formData, ...rest }) => {
          if (formData.type !== 'Syndic') {
            return null
          }
          return (
            <div>
              <SelectInput
                fullWidth
                source="typesyndic_type_contrat"
                label="Type de contrat"
                variant={props.variant}
                choices={[
                  { id: 'Connect', name: 'Connect' },
                  { id: 'Comfort', name: 'Comfort' },
                  { id: 'Collection', name: 'Collection' },
                ]}
              />
              <br />
              <NumberInput
                fullWidth
                source="typesyndic_nb_visites"
                label="Nombre de visites"
                variant={props.variant}
              />
              <br />
              <NumberInput
                fullWidth
                source="typesyndic_nb_reunions"
                label="Nombre de réunions"
                variant={props.variant}
              />
              <br />
              <NumberInput
                source="typesyndic_nb_ags"
                label="Nombre d'AGs"
                variant={props.variant}
              />
            </div>
          )
        }}
      </FormDataConsumer>
      <FormDataConsumer {...props}>
        {({ formData, ...rest }) => {
          if (formData.type !== 'Diagnostic') {
            return null
          }
          return (
            <>
              <ReferenceInput
                source="typediagnostic_presta_id"
                label="Prestataire"
                reference="prestas"
                variant={props.variant}
              >
                <SelectInput fullWidth optionText="name" />
              </ReferenceInput>
              <br />
              <ReferenceInput
                source="typediagnostic_presta_id"
                label="Prestataire"
                reference="prestas"
                variant={props.variant}
              >
                <AutocompleteInput
                  fullWidth
                  optionText="name"
                  matchSuggestion={() => true}
                />
              </ReferenceInput>
              <br />
              <DateInput
                fullWidth
                source="typediagnostic_derniere_verif_interne"
                label="Date de dernière vérif interne"
                variant={props.variant}
              />
              <br />
              <TextInput
                fullWidth
                source="typediagnostic_resultat"
                label="Résultat"
                variant={props.variant}
              />
              <br />
              <DateInput
                fullWidth
                source="typediagnostic_prochaine_realisation"
                label="Date de prochaine réalisation"
                variant={props.variant}
              />
            </>
          )
        }}
      </FormDataConsumer>
      <FormDataConsumer {...props}>
        {({ formData, ...rest }) => {
          if (formData.type !== 'Assurance') {
            return null
          }
          return (
            <>
              <br />
              <TextInput
                source="typeassurance_assureur"
                label="Assureur"
                variant={props.variant}
              />
              <br />
              <TextInput
                source="typeassurance_num_police"
                label="N° de police"
                variant={props.variant}
              />
              <br />
              <TextInput
                source="typeassurance_assureur_adresse"
                label="Adresse assureur"
                variant={props.variant}
              />
              <br />
              <TextInput
                source="typeassurance_assureur_email"
                label="Email assureur"
                variant={props.variant}
              />
              <br />
              <TextInput
                source="typeassurance_courtier_adresse"
                label="Adresse courtier"
                variant={props.variant}
              />
              <br />
              <TextInput
                source="typeassurance_courtier_email"
                label="Email courtier"
                variant={props.variant}
              />
              <br />
              <SelectInput
                source="typeassurance_statut"
                label="Statut"
                variant={props.variant}
                choices={[
                  { id: 'A jour', name: 'A jour' },
                  { id: 'Echu', name: 'Echu' },
                ]}
              />
              <br />
              <BooleanInput
                source="typeassurance_remettre_concurrence"
                label="A remettre en concurrence?"
                variant={props.variant}
              />
            </>
          )
        }}
      </FormDataConsumer>
      <FormDataConsumer {...props}>
        {({ formData, ...rest }) => {
          if (!formData) return null
          let contractTypes = []
          if ('Syndic' === formData.type)
            contractTypes = {
              contract: [
                FileWithContext.TYPE.CONTRACT.SYNDIC,
                FileWithContext.TYPE.CONTRACT.SYNDIC_SIGNED,
              ],
            }
          if ('General' === formData.type)
            contractTypes = {
              contract: [
                FileWithContext.TYPE.CONTRACT.WATER,
                FileWithContext.TYPE.CONTRACT.GAS,
                FileWithContext.TYPE.CONTRACT.CLEANING_COMMON_AREAS,
                FileWithContext.TYPE.CONTRACT.ELECTRICITY,
                FileWithContext.TYPE.CONTRACT.ELEVATOR,
                FileWithContext.TYPE.CONTRACT.BOILER_ROOM_MAINTENANCE,
                FileWithContext.TYPE.CONTRACT.DOOR_MANTINENCE,
                FileWithContext.TYPE.CONTRACT.PEST_CONTROL,
                FileWithContext.TYPE.CONTRACT.RAT_EXTERMINATION,
                FileWithContext.TYPE.CONTRACT.MAINTENANCE_DRY_RISERS,
                FileWithContext.TYPE.CONTRACT.DISCONNECTION_MAINTENANCE,
                FileWithContext.TYPE.CONTRACT.SMOKE_EXTRACTION_MAINTENANCE,
                FileWithContext.TYPE.CONTRACT.PUMP_MAINTENANCE,
                FileWithContext.TYPE.CONTRACT.WATER_METER_READING,
                FileWithContext.TYPE.CONTRACT.GAS_METER_READING,
                FileWithContext.TYPE.CONTRACT.EMERGENCY_LIGHTING,
                FileWithContext.TYPE.CONTRACT.PARKING_EXTRACTOR,
                FileWithContext.TYPE.CONTRACT.FILE_ALARM_MAINTENANCE,
                FileWithContext.TYPE.CONTRACT.FIRE_EXTINGUISHER_MAINTENANCE,
                FileWithContext.TYPE.CONTRACT.VMC_MAINTENANCE,
                FileWithContext.TYPE.CONTRACT.BOOSTER_MAINTENANCE,
                FileWithContext.TYPE.CONTRACT.ROOF_MAINTENANCE,
                FileWithContext.TYPE.CONTRACT.ARCHIVE_MANAGEMENT,
                FileWithContext.TYPE.CONTRACT.DISCONNEXION,
                FileWithContext.TYPE.OTHERS,
              ],
            }
          if ('Assurance' === formData.type)
            contractTypes = {
              contract: [FileWithContext.TYPE.CONTRACT.ASSURANCE],
            }
          if ('Diagnostic' === formData.type)
            contractTypes = {
              contract: [
                FileWithContext.TYPE.ASBESTOS_DIAGNOSIS,
                FileWithContext.TYPE.LEAD_DIAGNOSIS,
                FileWithContext.TYPE.TERMITE_DIAGNOSIS,
              ],
            }
          // @todo: find a better way to render the component with data on edit
          // At the moment I tryed to set the defaultValue with a useState, I try wit a property
          // before the component call and I also tryo to do it directly on the default value
          // nothing of that works it was required to refresh before seen the files.

          return (
            <>
              {formData.file_id &&
                (formData.copro_internal_id || coproInternalId) && (
                  <UpsertFileButton
                    resource="file_with_context"
                    documentType={FileWithContext.DOCUMENT_TYPE.CONTRACT}
                    issuedBy={FileWithContext.ISSUED_BY.HELLO_APP}
                    limitQuantity={1}
                    validTypes={contractTypes}
                    previewAttachedFiles={true}
                    copro_id={formData.copro_internal_id || coproInternalId}
                    defaultValue={[{ id: formData.file_id, ...formData }]}
                    saveResult={(result) => {
                      const [
                        {
                          id,
                          type,
                          amount_excl_vat,
                          amount_incl_vat,
                          purpose,
                          url,
                          issue_date,
                          deadline,
                        },
                      ] = result.accepted_files
                      let contractData = {}
                      if ('General' === formData.type)
                        contractData = {
                          typegeneral_montant_ht_annuel: amount_excl_vat,
                          typegeneral_montant_ttc_annuel: amount_incl_vat,
                          typegeneral_description: purpose,
                          typegeneral_fichier: [url],
                        }
                      if ('Syndic' === formData.type)
                        if (FileWithContext.TYPE.CONTRACT.SYNDIC === type)
                          contractData = {
                            typesyndic_date_debut: issue_date,
                            typesyndic_date_fin: deadline,
                            typesyndic_montant_ht_annuel: amount_excl_vat,
                            typesyndic_montant_ttc_annuel: amount_incl_vat,
                            typesyndic_fichier: [url],
                          }
                        else
                          contractData = {
                            typesyndic_date_debut: issue_date,
                            typesyndic_date_fin: deadline,
                            typesyndic_montant_ht_annuel: amount_excl_vat,
                            typesyndic_montant_ttc_annuel: amount_incl_vat,
                            typesyndic_fichier_signed: [url],
                          }
                      if ('Diagnostic' === formData.type)
                        contractData = {
                          typediagnostic_date_realisation: issue_date,
                          typediagnostic_date_expiration: deadline,
                          typesyndic_fichier: [url],
                        }
                      if ('Assurance' === formData.type)
                        contractData = {
                          typeassurance_date_echeance: deadline,
                          typeassurance_montant_ht_annuel: amount_excl_vat,
                          typeassurance_montant_ttc_annuel: amount_incl_vat,
                          typeassurance_fichier: [url],
                        }
                      setContractFileData({
                        contract_file_id: id,
                        ...contractData,
                      })
                    }}
                    show_shared_select={true}
                  />
                )}
              {!formData.file_id && (
                <UpsertFileButton
                  resource="file_with_context"
                  documentType={FileWithContext.DOCUMENT_TYPE.CONTRACT}
                  issuedBy={FileWithContext.ISSUED_BY.HELLO_APP}
                  limitQuantity={1}
                  copro_id={coproInternalId}
                  validTypes={contractTypes}
                  saveResult={(result) => {
                    const [
                      {
                        id,
                        type,
                        amount_excl_vat,
                        amount_incl_vat,
                        purpose,
                        url,
                        issue_date,
                        deadline,
                      },
                    ] = result.accepted_files
                    let contractData = {}
                    if ('General' === formData.type)
                      contractData = {
                        typegeneral_montant_ht_annuel: amount_excl_vat,
                        typegeneral_montant_ttc_annuel: amount_incl_vat,
                        typegeneral_description: purpose,
                        typegeneral_fichier: [url],
                      }
                    if ('Syndic' === formData.type)
                      if (FileWithContext.TYPE.CONTRACT.SYNDIC === type)
                        contractData = {
                          typesyndic_date_debut: issue_date,
                          typesyndic_date_fin: deadline,
                          typesyndic_montant_ht_annuel: amount_excl_vat,
                          typesyndic_montant_ttc_annuel: amount_incl_vat,
                          typesyndic_fichier: [url],
                        }
                      else
                        contractData = {
                          typesyndic_date_debut: issue_date,
                          typesyndic_date_fin: deadline,
                          typesyndic_montant_ht_annuel: amount_excl_vat,
                          typesyndic_montant_ttc_annuel: amount_incl_vat,
                          typesyndic_fichier_signed: [url],
                        }
                    if ('Diagnostic' === formData.type)
                      contractData = {
                        typediagnostic_date_realisation: issue_date,
                        typediagnostic_date_expiration: deadline,
                        typesyndic_fichier: [url],
                      }
                    if ('Assurance' === formData.type)
                      contractData = {
                        typeassurance_date_echeance: deadline,
                        typeassurance_montant_ht_annuel: amount_excl_vat,
                        typeassurance_montant_ttc_annuel: amount_incl_vat,
                        typeassurance_fichier: [url],
                      }
                    setContractFileData({
                      contract_file_id: id,
                      ...contractData,
                    })
                  }}
                  show_shared_select={true}
                />
              )}
            </>
          )
        }}
      </FormDataConsumer>
    </>
  )
}

export const ContractEdit = (props) => {
  return (
    <div>
      <Show {...props} title=" " actions={false}>
        <SimpleShowLayout style={{ padding: '1rem' }}>
          <TextField source="libelle" label="Copro" />
          <TextField source="type" label="Type de contrat" />
          <DateField source="created" label="Créé le" />
        </SimpleShowLayout>
      </Show>
      <Edit {...props} title=" ">
        <SimpleForm variant="outlined">
          <EditCommon {...props} />
        </SimpleForm>
      </Edit>
    </div>
  )
}

export const ContractCreate = (props) => {
  const [create] = useCreate()
  const [contractFileData, setContractFileData] = useState()
  const [coproInternalId, setCoproInternalId] = useState()

  const notify = useNotify()
  const redirect = useRedirect()

  const successHandler = (data) => {
    const { id, contract_file_id } = data
    const request = new Request(
      `${config.API_V1}/file_with_context/${contract_file_id}`,
      {
        method: 'PUT',
        headers: new Headers({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          linkable_id: id,
          linkable_type: 'contracts',
        }),
      }
    )
    fetch(request).then(() => {
      redirect('list', 'contracts')
    })
  }

  const save = useCallback(
    async (values) => {
      if (!contractFileData.contract_file_id) {
        notify('hs.file.errors.contract_creation', 'error')

        return
      }

      try {
        create(
          'contracts',
          { data: { ...values, ...contractFileData } },
          { onSuccess: successHandler }
        )
      } catch (error) {
        if (error.body.errors) {
          return error.body.errors
        }
      }
    },
    [create]
  )

  return (
    <Create {...props} title=" ">
      <SimpleForm
        onSubmit={save}
        toolbar={<CustomToolbar />}
        variant="outlined"
      >
        <ReferenceInput
          label="Copro"
          source="copro_id"
          reference="copros_monday"
          validate={[required()]}
        >
          <AutocompleteInput
            fullWidth
            optionValue="id"
            optionText="code"
            matchSuggestion={() => true}
            onChange={(e) => {
              Api.copros.getByMondayId(e).then((value) => {
                setCoproInternalId(value.data.copro_internal_id)
              })
            }}
          />
        </ReferenceInput>
        <SelectInput
          fullWidth
          source="type"
          label="Type de contrat"
          validate={[required()]}
          choices={[
            { id: 'General', name: 'General' },
            { id: 'Syndic', name: 'Syndic' },
            { id: 'Diagnostic', name: 'Diagnostic' },
            { id: 'Assurance', name: 'Assurance' },
          ]}
        />
        {coproInternalId && (
          <EditCommon
            setContractFileData={setContractFileData}
            coproInternalId={coproInternalId}
            {...props}
          />
        )}
        <br />
      </SimpleForm>
    </Create>
  )
}
